import Guid from "_shared/guids/guid";
import { LoaderFunctionArgs, redirect } from "react-router";

type Options = {
  // Where to go if URL shape is invalid
  baseRedirect: string;                 
  // Require :id?
  requireId?: boolean;
  // Validate :action
  allowedActions?: readonly string[];
  // Actions that don't require :id
  actionsWithoutId?: readonly string[];
};

export function makeFeeScheduleLoader(opts: Options) {
  const {
    baseRedirect,
    requireId = false,
    allowedActions,
    actionsWithoutId = [],
  } = opts;

  return async function loader({ params }: LoaderFunctionArgs) {
    const id = params["id"];
    const action = params["action"];

    if (allowedActions && action && !allowedActions.includes(action)) {
      return redirect(baseRedirect);
    }

    const needsId =
      requireId ||
      (action ? !actionsWithoutId.includes(action) : requireId);

    if (needsId) {
      if (!id) {
        console.warn("Missing fee schedule id in URL");
        return redirect(baseRedirect);
      }
      if (!Guid.isValid(id)) {
        console.warn(`Invalid fee schedule id format: ${id}`);
        return redirect(baseRedirect);
      }
    }

    return null;
  };
}

export function withDefaultTab(
  defaultTab: string,
  inner?: (a: LoaderFunctionArgs) => Promise<Response | null> | Response | null
) {
  return async (args: LoaderFunctionArgs) => {
    const { request, params } = args;

    if (!params.pageTab) {
      const url = new URL(request.url);
      const basePath = url.pathname.replace(/\/+$/, ""); // trim trailing slash
      return redirect(`${basePath}/${defaultTab}${url.search}`);
    }

    return inner ? inner(args) : null;
  };
}

export const feeScheduleLoader = makeFeeScheduleLoader({
  baseRedirect: "/fee-schedules",
});

export const templateFeeScheduleLoader = makeFeeScheduleLoader({
  baseRedirect: "/fee-schedules/templates",
  allowedActions: ["create", "edit"],
  actionsWithoutId: ["create"], // create has no id
  requireId: false,
});
  
export const activeFeeScheduleLoader = makeFeeScheduleLoader({
  baseRedirect: "/fee-schedules/active",
  requireId: true,
});

export const archivedFeeScheduleLoader = makeFeeScheduleLoader({
  baseRedirect: "/fee-schedules/archived",
  requireId: true
});
